/**
 * Service Staff Dashboard - Compact Mobile-first view
 * Features:
 * - Small, informative order cards
 * - Order locking (only acceptor can mark as served)
 * - Dynamic clock timer
 * - No duplicate tabs/stats
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Clock, CheckCircle2, Play, Check, User, Timer, RefreshCw, 
  Bell, Coffee, DoorOpen, LogOut, AlertCircle
} from 'lucide-react';

// Format time
const formatTime = (dateStr) => {
  if (!dateStr) return '--:--';
  return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

// Compact Timer
const CompactTimer = ({ startTime, targetMinutes }) => {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    if (!startTime) return;
    const start = new Date(startTime).getTime();
    const updateTimer = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const isOvertime = mins >= (targetMinutes || 30);
  
  return (
    <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${isOvertime ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
      {mins}:{secs.toString().padStart(2, '0')}
    </span>
  );
};

// Compact Order Row
const OrderRow = ({ order, onAccept, onServe, currentUserId, showTimeline }) => {
  const isAccepted = ['accepted', 'in_progress'].includes(order.status);
  const isServed = ['served', 'completed'].includes(order.status);
  const canServe = isAccepted && !isServed && order.accepted_by === currentUserId;
  const serviceTime = order.service?.serve_time || order.estimated_serve_time || 30;

  return (
    <div 
      className={`flex items-center gap-3 p-3 mb-2 rounded-lg border-l-4 bg-white shadow-sm ${
        isServed ? 'border-l-green-500' : isAccepted ? 'border-l-blue-500' : 'border-l-yellow-500'
      }`}
      data-testid={`order-row-${order.id}`}
    >
      {/* Room Number */}
      <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-indigo-100 flex flex-col items-center justify-center">
        <DoorOpen className="h-4 w-4 text-indigo-600" />
        <span className="font-bold text-sm text-indigo-800">{order.room_number || 'N/A'}</span>
      </div>

      {/* Order Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-sm text-gray-800 truncate">{order.service_name || order.service?.name || 'Service'}</p>
          {order.quantity > 1 && <Badge variant="outline" className="text-xs px-1">x{order.quantity}</Badge>}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
          <User className="h-3 w-3" />
          <span className="truncate">{order.guest_name || 'Guest'}</span>
          <span>•</span>
          <Clock className="h-3 w-3" />
          <span>{formatTime(order.created_at)}</span>
        </div>
        {order.notes && (
          <p className="text-xs text-gray-500 italic truncate mt-0.5">"{order.notes}"</p>
        )}
        
        {/* Timeline for completed orders */}
        {showTimeline && isServed && (
          <div className="flex items-center gap-2 text-xs mt-1">
            <span className="text-gray-400">→ {formatTime(order.accepted_at)}</span>
            <span className="text-gray-400">→ {formatTime(order.served_at)}</span>
            <Badge className={`text-xs px-1 py-0 ${order.served_within_time ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
              {order.served_within_time ? 'On Time' : 'Delayed'}
            </Badge>
          </div>
        )}
        
        {/* Accepted by info */}
        {isAccepted && !isServed && order.accepted_by_name && (
          <p className="text-xs text-blue-600 mt-0.5">By: {order.accepted_by_name}</p>
        )}
      </div>

      {/* Timer & Actions */}
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        {isAccepted && !isServed && (
          <CompactTimer startTime={order.accepted_at} targetMinutes={serviceTime} />
        )}
        
        {order.status === 'pending' && (
          <Button size="sm" onClick={() => onAccept(order.id)} className="h-8 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs">
            <Play className="h-3 w-3 mr-1" />Accept
          </Button>
        )}
        
        {isAccepted && !isServed && canServe && (
          <Button size="sm" onClick={() => onServe(order.id)} className="h-8 px-3 bg-green-600 hover:bg-green-700 text-white text-xs">
            <Check className="h-3 w-3 mr-1" />Serve
          </Button>
        )}
        
        {isAccepted && !isServed && !canServe && (
          <Badge variant="outline" className="text-xs text-gray-400">
            <AlertCircle className="h-3 w-3 mr-1" />Locked
          </Badge>
        )}
        
        {isServed && (
          <CheckCircle2 className={`h-5 w-5 ${order.served_within_time ? 'text-green-500' : 'text-orange-500'}`} />
        )}
      </div>
    </div>
  );
};

export default function ServiceStaffDashboard({ user }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [refreshing, setRefreshing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const currentUserId = user?.id || user?.user_id || user?.email;

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const res = await api.get('/service-orders/my-orders');
      setOrders(res.data || []);
    } catch (error) {
      try {
        const fallbackRes = await api.get('/service-orders');
        setOrders(fallbackRes.data || []);
      } catch (e) {
        toast.error('Failed to load orders');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 15000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  const handleRefresh = () => { setRefreshing(true); loadOrders(); };
  
  const handleAccept = async (orderId) => {
    try {
      await api.put(`/service-orders/${orderId}/accept`);
      toast.success('Order accepted!');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    }
  };

  const handleServe = async (orderId) => {
    try {
      await api.put(`/service-orders/${orderId}/serve`);
      toast.success('Order served!');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark served');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const pendingOrders = orders.filter(o => o.status === 'pending');
  const activeOrders = orders.filter(o => ['accepted', 'in_progress'].includes(o.status));
  const completedOrders = orders.filter(o => ['served', 'completed'].includes(o.status));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="service-staff-dashboard">
      {/* Compact Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-4 py-3 sticky top-0 z-10 shadow-lg">
        <div className="flex justify-between items-center max-w-lg mx-auto">
          <div className="flex items-center gap-2">
            <Coffee className="h-5 w-5" />
            <span className="font-bold">Service Orders</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm bg-white/20 px-2 py-1 rounded">
              {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={refreshing} className="h-8 w-8 text-white hover:bg-white/20">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="h-8 w-8 text-white hover:bg-red-500/50">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-lg mx-auto px-3 py-3">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 h-10 bg-white shadow-sm mb-3">
            <TabsTrigger value="pending" className="text-xs data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-800">
              <Bell className="h-3 w-3 mr-1" />
              New ({pendingOrders.length})
            </TabsTrigger>
            <TabsTrigger value="active" className="text-xs data-[state=active]:bg-blue-100 data-[state=active]:text-blue-800">
              <Timer className="h-3 w-3 mr-1" />
              Active ({activeOrders.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="text-xs data-[state=active]:bg-green-100 data-[state=active]:text-green-800">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Done ({completedOrders.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-0">
            {pendingOrders.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Bell className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No pending orders</p>
              </div>
            ) : (
              pendingOrders.map(order => (
                <OrderRow key={order.id} order={order} onAccept={handleAccept} onServe={handleServe} currentUserId={currentUserId} />
              ))
            )}
          </TabsContent>

          <TabsContent value="active" className="mt-0">
            {activeOrders.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Timer className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No active orders</p>
              </div>
            ) : (
              activeOrders.map(order => (
                <OrderRow key={order.id} order={order} onAccept={handleAccept} onServe={handleServe} currentUserId={currentUserId} />
              ))
            )}
          </TabsContent>

          <TabsContent value="completed" className="mt-0">
            {completedOrders.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <CheckCircle2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No completed orders</p>
              </div>
            ) : (
              completedOrders.slice(0, 20).map(order => (
                <OrderRow key={order.id} order={order} onAccept={handleAccept} onServe={handleServe} currentUserId={currentUserId} showTimeline />
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
